// modals/quotations/EditQuotation.jsx
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { DatePicker } from 'antd';
import { Trash2, Save,  RotateCcw,Search } from 'react-feather';
import Select from 'react-select';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { quotationActions, quotationProductActions } from '../../action/quotationActions';
import { customerActions } from '../../action/customerActions';

const EditQuotation = ({ quotationId, onSuccess, onClose, isOpen = false }) => {
    const modalRef = useRef(null);
    
    const roundAmount = (amount) => {
        return Math.round(amount);
    };
    
    const [formData, setFormData] = useState({
        id: '',
        quotation_no: '',
        reference_no: '',
        quotation_date: dayjs().format('YYYY-MM-DD'),
        expiry_date: dayjs().add(15, 'day').format('YYYY-MM-DD'),
        customer_id: '',
        customer_name: '',
        customer_phone: '',
        subtotal: 0,
        tax: 0,
        discount: 0,
        shipping: 0,
        adjustment: 0,
        grand_total: 0,
        rounded_grand_total: 0,
        round_off: 0,
        enable_rounding: true,
        no_of_items: 0,
        total_qty: 0,
        notes: '',
        terms_conditions: '',
        status: 'draft',
        items: []
    });

    const [originalData, setOriginalData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [selectedExpiryDate, setSelectedExpiryDate] = useState(dayjs().add(15, 'day'));
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showProductSearch, setShowProductSearch] = useState(false);
    const [error, setError] = useState(null);

    const statusOptions = [
        { value: 'draft', label: 'Draft' },
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' }
    ];

    const cleanupModalBackdrops = () => {
        const backdrops = document.querySelectorAll('.modal-backdrop');
        backdrops.forEach(backdrop => backdrop.remove());
        document.body.classList.remove('modal-open');
        document.body.style.paddingRight = '';
        document.body.style.overflow = '';
    };

    const showBootstrapModal = () => {
        cleanupModalBackdrops();
        const modalElement = document.getElementById('edit-quotation');
        if (modalElement) {
            if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
                window.$(modalElement).modal('show');
            } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
                let modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                if (!modalInstance) {
                    modalInstance = new window.bootstrap.Modal(modalElement);
                }
                modalInstance.show();
            } else {
                modalElement.classList.add('show');
                modalElement.style.display = 'block';
                const backdrop = document.createElement('div');
                backdrop.className = 'modal-backdrop fade show';
                document.body.appendChild(backdrop);
                document.body.classList.add('modal-open');
            }
        }
    };

    const hideBootstrapModal = () => {
        const modalElement = document.getElementById('edit-quotation');
        if (modalElement) {
            if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
                window.$(modalElement).modal('hide');
                setTimeout(cleanupModalBackdrops, 300);
            } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
                const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                if (modalInstance) {
                    modalInstance.hide();
                    setTimeout(cleanupModalBackdrops, 300);
                }
            } else {
                modalElement.classList.remove('show');
                modalElement.style.display = 'none';
                cleanupModalBackdrops();
            }
        }
    };

    const loadQuotationData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const result = await quotationActions.getQuotation(quotationId);
            
            if (result.success) {
                const quotation = result.data.quotation || result.data;
                const items = result.data.items || [];
                
                const formattedData = {
                    id: quotation.id,
                    quotation_no: quotation.quotation_no,
                    reference_no: quotation.reference_no,
                    quotation_date: quotation.quotation_date || dayjs().format('YYYY-MM-DD'),
                    expiry_date: quotation.expiry_date || dayjs().add(15, 'day').format('YYYY-MM-DD'),
                    customer_id: quotation.customer_id,
                    customer_name: quotation.customer_name,
                    customer_phone: quotation.customer_phone,
                    subtotal: parseFloat(quotation.subtotal || 0),
                    tax: parseFloat(quotation.tax || 0),
                    discount: parseFloat(quotation.discount || 0),
                    shipping: parseFloat(quotation.shipping || 0),
                    adjustment: parseFloat(quotation.adjustment || 0),
                    grand_total: parseFloat(quotation.grand_total || 0),
                    rounded_grand_total: parseFloat(quotation.rounded_grand_total || 0),
                    round_off: parseFloat(quotation.round_off || 0),
                    enable_rounding: quotation.enable_rounding !== undefined ? quotation.enable_rounding : true,
                    no_of_items: items.length,
                    total_qty: items.reduce((sum, i) => sum + (i.quantity || 0), 0),
                    notes: quotation.notes || '',
                    terms_conditions: quotation.terms_conditions || '',
                    status: quotation.status || 'draft',
                    items: items.map(item => ({
                        id: item.id,
                        product_id: item.product_id,
                        product_code: item.product_code || item.sku,
                        product_name: item.product_name,
                        sku: item.sku,
                        units: item.units || 'PCS',
                        quantity: parseFloat(item.quantity || 0),
                        unit_price: parseFloat(item.unit_price || 0),
                        discount: parseFloat(item.discount || 0),
                        discount_amount: parseFloat(item.discount_amount || 0),
                        tax_percent: parseFloat(item.tax_percent || 0),
                        tax_amount: parseFloat(item.tax_amount || 0),
                        sgst_percent: parseFloat(item.sgst_percent || 0),
                        sgst_amount: parseFloat(item.sgst_amount || 0),
                        cgst_percent: parseFloat(item.cgst_percent || 0),
                        cgst_amount: parseFloat(item.cgst_amount || 0),
                        igst_amount: parseFloat(item.igst_amount || 0),
                        unit_cost: parseFloat(item.unit_cost || 0),
                        total_cost: parseFloat(item.total_cost || 0),
                        category_id: item.category_id,
                        brand_id: item.brand_id
                    }))
                };
                
                setFormData(formattedData);
                setOriginalData(formattedData);
                
                if (quotation.customer_id) {
                    setSelectedCustomer({
                        value: quotation.customer_id,
                        label: quotation.customer_name,
                        phone: quotation.customer_phone
                    });
                }
                
                if (quotation.quotation_date) {
                    setSelectedDate(dayjs(quotation.quotation_date));
                }
                
                if (quotation.expiry_date) {
                    setSelectedExpiryDate(dayjs(quotation.expiry_date));
                }
                
                setTimeout(() => showBootstrapModal(), 100);
                
            } else {
                setError(result.message || 'Failed to load quotation data');
                Swal.fire('Error', result.message || 'Failed to load quotation data', 'error');
            }
        } catch (error) {
            console.error('Error loading quotation:', error);
            setError('Failed to load quotation data');
            Swal.fire('Error', 'Failed to load quotation data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const loadCustomers = async () => {
        try {
            const result = await customerActions.getCustomers({ limit: 1000 });
            if (result.success) {
                setCustomers([
                    { value: '', label: 'Select Customer' },
                    ...result.data.map(c => ({
                        value: c.id,
                        label: `${c.name} (${c.phone || 'No phone'})`,
                        phone: c.phone,
                        email: c.email,
                        address: c.address,
                        ...c
                    }))
                ]);
            }
        } catch (error) {
            console.error('Error loading customers:', error);
        }
    };

    useEffect(() => {
        if (quotationId) {
            loadQuotationData();
            loadCustomers();
        }
        
        return () => {
            cleanupModalBackdrops();
        };
    }, [quotationId]);

    useEffect(() => {
        if (isOpen && !loading && formData.id) {
            setTimeout(() => showBootstrapModal(), 100);
        }
    }, [isOpen, loading, formData.id]);

    const calculateTotals = (items, discount = 0, shipping = 0, adjustment = 0, enableRounding = true) => {
        const no_of_items = items.length;
        const total_qty = items.reduce((sum, i) => sum + (i.quantity || 0), 0);
        
        const subtotal = items.reduce((sum, i) => sum + (i.unit_price * i.quantity), 0);
        const totalTax = items.reduce((sum, i) => sum + (i.tax_amount || 0), 0);
        
        const grandTotal = subtotal - discount + totalTax + shipping + adjustment;
        
        let roundedGrandTotal = grandTotal;
        let roundOff = 0;
        
        if (enableRounding) {
            roundedGrandTotal = roundAmount(grandTotal);
            roundOff = roundedGrandTotal - grandTotal;
        }

        return {
            no_of_items,
            total_qty,
            subtotal: parseFloat(subtotal.toFixed(2)),
            tax: parseFloat(totalTax.toFixed(2)),
            grand_total: parseFloat(grandTotal.toFixed(2)),
            rounded_grand_total: parseFloat(roundedGrandTotal.toFixed(2)),
            round_off: parseFloat(roundOff.toFixed(2))
        };
    };

    const handleQuantityChange = (index, value) => {
        const qty = parseFloat(value) || 0;
        
        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };
            
            const actualQty = Math.max(0, qty);
            
            const subtotal = item.unit_price * actualQty;
            const discountAmount = (subtotal * (item.discount || 0)) / 100;
            const taxableAmount = subtotal - discountAmount;
            const taxAmount = (taxableAmount * (item.tax_percent || 0)) / 100;
            const totalCost = taxableAmount + taxAmount;
            
            updatedItems[index] = {
                ...item,
                quantity: actualQty,
                discount_amount: discountAmount,
                tax_amount: taxAmount,
                total_cost: totalCost
            };
            
            const totals = calculateTotals(updatedItems, prev.discount, prev.shipping, prev.adjustment, prev.enable_rounding);
            
            return {
                ...prev,
                items: updatedItems,
                ...totals
            };
        });
    };

    const handlePriceChange = (index, value) => {
        const price = parseFloat(value) || 0;
        
        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };
            
            const subtotal = price * item.quantity;
            const discountAmount = (subtotal * (item.discount || 0)) / 100;
            const taxableAmount = subtotal - discountAmount;
            const taxAmount = (taxableAmount * (item.tax_percent || 0)) / 100;
            const totalCost = taxableAmount + taxAmount;
            
            updatedItems[index] = {
                ...item,
                unit_price: price,
                discount_amount: discountAmount,
                tax_amount: taxAmount,
                total_cost: totalCost
            };
            
            const totals = calculateTotals(updatedItems, prev.discount, prev.shipping, prev.adjustment, prev.enable_rounding);
            
            return {
                ...prev,
                items: updatedItems,
                ...totals
            };
        });
    };

    const handleDiscountChange = (index, value) => {
        const discountPercent = parseFloat(value) || 0;
        
        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };
            
            const subtotal = item.unit_price * item.quantity;
            const discountAmount = (subtotal * discountPercent) / 100;
            const taxableAmount = subtotal - discountAmount;
            const taxAmount = (taxableAmount * (item.tax_percent || 0)) / 100;
            const totalCost = taxableAmount + taxAmount;
            
            updatedItems[index] = {
                ...item,
                discount: discountPercent,
                discount_amount: discountAmount,
                tax_amount: taxAmount,
                total_cost: totalCost
            };
            
            const totals = calculateTotals(updatedItems, prev.discount, prev.shipping, prev.adjustment, prev.enable_rounding);
            
            return {
                ...prev,
                items: updatedItems,
                ...totals
            };
        });
    };

    const handleTaxChange = (index, value) => {
        const taxPercent = parseFloat(value) || 0;
        
        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };
            
            const taxableAmount = item.unit_price * item.quantity - (item.discount_amount || 0);
            const taxAmount = (taxableAmount * taxPercent) / 100;
            const totalCost = taxableAmount + taxAmount;
            
            updatedItems[index] = {
                ...item,
                tax_percent: taxPercent,
                tax_amount: taxAmount,
                total_cost: totalCost
            };
            
            const totals = calculateTotals(updatedItems, prev.discount, prev.shipping, prev.adjustment, prev.enable_rounding);
            
            return {
                ...prev,
                items: updatedItems,
                ...totals
            };
        });
    };

    const handleRemoveItem = (index) => {
        Swal.fire({
            title: 'Remove Item?',
            text: 'Are you sure you want to remove this item?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, remove it!'
        }).then((result) => {
            if (result.isConfirmed) {
                setFormData(prev => {
                    const updatedItems = prev.items.filter((_, i) => i !== index);
                    const totals = calculateTotals(updatedItems, prev.discount, prev.shipping, prev.adjustment, prev.enable_rounding);
                    
                    return {
                        ...prev,
                        items: updatedItems,
                        ...totals
                    };
                });
                
                Swal.fire('Removed!', 'Item removed.', 'success');
            }
        });
    };

    const handleProductSearch = async (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        
        if (term.length >= 2) {
            const result = await quotationProductActions.searchProducts(term);
            if (result.success) {
                setSearchResults(result.data);
                setShowProductSearch(true);
            }
        } else {
            setSearchResults([]);
            setShowProductSearch(false);
        }
    };

    const handleAddProduct = (product) => {
        const existingItem = formData.items.find(i => i.product_id === product.id);
        
        if (existingItem) {
            handleQuantityChange(
                formData.items.indexOf(existingItem),
                existingItem.quantity + 1
            );
            setSearchTerm('');
            setShowProductSearch(false);
            return;
        }

        const unitPrice = parseFloat(product.selling_price || 0);
        const taxPercent = parseFloat(product.tax_rate || 0);
        
        const subtotal = unitPrice * 1;
        const discountAmount = 0;
        const taxableAmount = subtotal - discountAmount;
        const taxAmount = (taxableAmount * taxPercent) / 100;
        const totalCost = taxableAmount + taxAmount;

        const newItem = {
            id: `temp_${Date.now()}`,
            product_id: product.id,
            product_code: product.sku,
            product_name: product.product_name,
            sku: product.sku,
            units: 'PCS',
            quantity: 1,
            unit_price: unitPrice,
            discount: 0,
            discount_amount: 0,
            tax_percent: taxPercent,
            tax_amount: taxAmount,
            unit_cost: parseFloat(product.cost_price || 0),
            total_cost: totalCost,
            category_id: product.category_id,
            brand_id: product.brand_id
        };

        setFormData(prev => {
            const updatedItems = [...prev.items, newItem];
            const totals = calculateTotals(updatedItems, prev.discount, prev.shipping, prev.adjustment, prev.enable_rounding);
            
            return {
                ...prev,
                items: updatedItems,
                ...totals
            };
        });

        setSearchTerm('');
        setShowProductSearch(false);
    };

    const handleCustomerChange = (option) => {
        setSelectedCustomer(option);
        setFormData(prev => ({ 
            ...prev, 
            customer_id: option?.value || '',
            customer_name: option?.label || '',
            customer_phone: option?.phone || ''
        }));
    };

    const handleDateChange = (date) => {
        if (date) {
            setSelectedDate(date);
            setFormData(prev => ({ ...prev, quotation_date: date.format('YYYY-MM-DD') }));
        }
    };

    const handleExpiryDateChange = (date) => {
        if (date) {
            setSelectedExpiryDate(date);
            setFormData(prev => ({ ...prev, expiry_date: date.format('YYYY-MM-DD') }));
        }
    };

    const handleStatusChange = (option) => {
        setFormData(prev => ({ ...prev, status: option?.value || 'draft' }));
    };

    const handleDiscountAmountChange = (value) => {
        const discount = parseFloat(value) || 0;
        setFormData(prev => {
            const totals = calculateTotals(prev.items, discount, prev.shipping, prev.adjustment, prev.enable_rounding);
            return {
                ...prev,
                discount,
                ...totals
            };
        });
    };

    const handleShippingChange = (value) => {
        const shipping = parseFloat(value) || 0;
        setFormData(prev => {
            const totals = calculateTotals(prev.items, prev.discount, shipping, prev.adjustment, prev.enable_rounding);
            return {
                ...prev,
                shipping,
                ...totals
            };
        });
    };

    const handleAdjustmentChange = (value) => {
        const adjustment = parseFloat(value) || 0;
        setFormData(prev => {
            const totals = calculateTotals(prev.items, prev.discount, prev.shipping, adjustment, prev.enable_rounding);
            return {
                ...prev,
                adjustment,
                ...totals
            };
        });
    };

    const handleToggleRounding = () => {
        setFormData(prev => {
            const newEnableRounding = !prev.enable_rounding;
            const totals = calculateTotals(prev.items, prev.discount, prev.shipping, prev.adjustment, newEnableRounding);
            
            return {
                ...prev,
                enable_rounding: newEnableRounding,
                ...totals
            };
        });
    };

    const validateForm = () => {
        if (!formData.customer_id) {
            Swal.fire('Error', 'Please select a customer', 'error');
            return false;
        }
        
        if (formData.items.length === 0) {
            Swal.fire('Error', 'Please add at least one item', 'error');
            return false;
        }
        
        return true;
    };

    const hasChanges = () => {
        if (!originalData) return false;
        
        if (formData.customer_id !== originalData.customer_id) return true;
        if (formData.quotation_date !== originalData.quotation_date) return true;
        if (formData.expiry_date !== originalData.expiry_date) return true;
        if (formData.status !== originalData.status) return true;
        if (formData.notes !== originalData.notes) return true;
        if (formData.terms_conditions !== originalData.terms_conditions) return true;
        if (formData.discount !== originalData.discount) return true;
        if (formData.shipping !== originalData.shipping) return true;
        if (formData.adjustment !== originalData.adjustment) return true;
        if (formData.enable_rounding !== originalData.enable_rounding) return true;
        
        if (formData.items.length !== originalData.items.length) return true;
        
        for (let i = 0; i < formData.items.length; i++) {
            const newItem = formData.items[i];
            const oldItem = originalData.items[i];
            
            if (!oldItem) return true;
            
            if (newItem.product_id !== oldItem.product_id) return true;
            if (newItem.quantity !== oldItem.quantity) return true;
            if (newItem.unit_price !== oldItem.unit_price) return true;
            if (newItem.discount !== oldItem.discount) return true;
            if (newItem.tax_percent !== oldItem.tax_percent) return true;
        }
        
        return false;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        
        if (!hasChanges()) {
            Swal.fire('Info', 'No changes to save', 'info');
            return;
        }
        
        try {
            setSaving(true);
            
            const itemsToSend = formData.items.map(item => {
                const { id, ...rest } = item;
                if (id && !id.toString().startsWith('temp_')) {
                    return { ...rest, id };
                }
                return rest;
            });
            
            const finalGrandTotal = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;
            
            const dataToSend = {
                ...formData,
                items: itemsToSend,
                grand_total: finalGrandTotal,
                rounded_grand_total: formData.rounded_grand_total,
                round_off: formData.round_off,
                enable_rounding: formData.enable_rounding
            };
            
            const result = await quotationActions.updateQuotation(quotationId, dataToSend);
            
            if (result.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Quotation updated successfully',
                    timer: 2000
                });
                
                hideBootstrapModal();
                if (onSuccess) onSuccess();
            } else {
                Swal.fire('Error', result.message || 'Failed to update quotation', 'error');
            }
        } catch (error) {
            console.error('Error updating quotation:', error);
            Swal.fire('Error', 'Failed to update quotation', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (hasChanges()) {
            Swal.fire({
                title: 'Discard Changes?',
                text: 'You have unsaved changes. Are you sure?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, discard'
            }).then((result) => {
                if (result.isConfirmed) {
                    hideBootstrapModal();
                    if (onClose) onClose();
                }
            });
        } else {
            hideBootstrapModal();
            if (onClose) onClose();
        }
    };

    const formatCurrency = (amount) => {
        return `₹${parseFloat(amount || 0).toFixed(2)}`;
    };

    if (loading) {
        return (
            <div className="modal fade" id="edit-quotation" ref={modalRef} tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Quotation</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body text-center p-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Loading quotation data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !formData.id) {
        return (
            <div className="modal fade" id="edit-quotation" ref={modalRef} tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title text-danger">Error</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            <p>{error || 'Failed to load quotation data'}</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={onClose}>Close</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="modal fade" id="edit-quotation" ref={modalRef} tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Edit Quotation - {formData.quotation_no}</h5>
                        <button type="button" className="btn-close" onClick={handleCancel}></button>
                    </div>
                    
                    <div className="modal-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row g-3 mb-3">
                                <div className="col-md-3">
                                    <label className="form-label fw-bold">Quotation No</label>
                                    <input
                                        type="text"
                                        className="form-control bg-light"
                                        value={formData.quotation_no}
                                        readOnly
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label fw-bold">Reference No</label>
                                    <input
                                        type="text"
                                        className="form-control bg-light"
                                        value={formData.reference_no}
                                        readOnly
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label fw-bold">Quotation Date</label>
                                    <DatePicker
                                        value={selectedDate}
                                        onChange={handleDateChange}
                                        format="DD/MM/YYYY"
                                        className="form-control"
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label fw-bold">Valid Till</label>
                                    <DatePicker
                                        value={selectedExpiryDate}
                                        onChange={handleExpiryDateChange}
                                        format="DD/MM/YYYY"
                                        className="form-control"
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Customer Name</label>
                                    <Select
                                        options={customers}
                                        value={selectedCustomer}
                                        onChange={handleCustomerChange}
                                        placeholder="Select Customer"
                                        isClearable
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label fw-bold">Phone</label>
                                    <input
                                        type="text"
                                        className="form-control bg-light"
                                        value={formData.customer_phone}
                                        readOnly
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label fw-bold">Status</label>
                                    <Select
                                        options={statusOptions}
                                        value={statusOptions.find(s => s.value === formData.status)}
                                        onChange={handleStatusChange}
                                        placeholder="Select status"
                                    />
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold">Add Product</label>
                                <div className="position-relative">
                                    <div className="input-group">
                                        <span className="input-group-text bg-white">
                                            <Search size={16} className="text-muted" />
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search product by name or code..."
                                            value={searchTerm}
                                            onChange={handleProductSearch}
                                        />
                                    </div>
                                    
                                    {showProductSearch && searchResults.length > 0 && (
                                        <div className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm" style={{ zIndex: 1000, maxHeight: '300px', overflowY: 'auto' }}>
                                            {searchResults.map(product => (
                                                <div
                                                    key={product.id}
                                                    className="p-2 border-bottom"
                                                    onClick={() => handleAddProduct(product)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div className="d-flex justify-content-between">
                                                        <div>
                                                            <strong>{product.product_name}</strong>
                                                            <br />
                                                            <small className="text-muted">Code: {product.sku}</small>
                                                        </div>
                                                        <div className="text-end">
                                                            <span className="text-primary">{formatCurrency(product.selling_price)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {formData.items.length > 0 && (
                                <div className="mb-4">
                                    <div className="table-responsive border rounded">
                                        <table className="table table-bordered mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Product Code</th>
                                                    <th>Product Name</th>
                                                    <th>Units</th>
                                                    <th className="text-end">Qty</th>
                                                    <th className="text-end">Rate</th>
                                                    <th className="text-end">Disc %</th>
                                                    <th className="text-end">GST %</th>
                                                    <th className="text-end">Amount</th>
                                                    <th className="text-center">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formData.items.map((item, index) => (
                                                    <tr key={item.id || index}>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm bg-light"
                                                                value={item.product_code || ''}
                                                                readOnly
                                                            />
                                                        </td>
                                                        <td>
                                                            <strong>{item.product_name}</strong>
                                                        </td>
                                                        <td>
                                                            <select
                                                                className="form-select form-select-sm"
                                                                value={item.units || 'PCS'}
                                                                onChange={(e) => {
                                                                    const updated = [...formData.items];
                                                                    updated[index].units = e.target.value;
                                                                    setFormData(prev => ({ ...prev, items: updated }));
                                                                }}
                                                            >
                                                                <option value="PCS">PCS</option>
                                                                <option value="BOX">BOX</option>
                                                                <option value="KG">KG</option>
                                                                <option value="LTR">LTR</option>
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm text-end"
                                                                value={item.quantity}
                                                                onChange={(e) => handleQuantityChange(index, e.target.value)}
                                                                min="0"
                                                                step="1"
                                                                style={{ width: '80px' }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm text-end"
                                                                value={item.unit_price}
                                                                onChange={(e) => handlePriceChange(index, e.target.value)}
                                                                min="0"
                                                                step="1"
                                                                style={{ width: '100px' }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm text-end"
                                                                value={item.discount || 0}
                                                                onChange={(e) => handleDiscountChange(index, e.target.value)}
                                                                min="0"
                                                                step="1"
                                                                style={{ width: '70px' }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm text-end"
                                                                value={item.tax_percent || 0}
                                                                onChange={(e) => handleTaxChange(index, e.target.value)}
                                                                min="0"
                                                                step="1"
                                                                style={{ width: '70px' }}
                                                            />
                                                        </td>
                                                        <td className="text-end fw-bold">
                                                            {formatCurrency(item.total_cost)}
                                                        </td>
                                                        <td className="text-center">
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-danger btn-sm"
                                                                onClick={() => handleRemoveItem(index)}
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <div className="row">
                                        <div className="col-md-6 mb-2">
                                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                <span className="fw-semibold">No of Items</span>
                                                <span className="fw-bold text-primary">{formData.no_of_items}</span>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                <span className="fw-semibold">Total Qty</span>
                                                <span className="fw-bold text-info">{formData.total_qty}</span>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                <span className="fw-semibold">Subtotal</span>
                                                <span className="fw-bold text-success">{formatCurrency(formData.subtotal)}</span>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                <span className="fw-semibold">Tax</span>
                                                <span className="fw-bold text-warning">{formatCurrency(formData.tax)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="bg-primary text-white p-3 rounded">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h5 className="mb-0 text-white">Total Amount</h5>
                                            <div className="d-flex align-items-center">
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm ${formData.enable_rounding ? 'btn-light' : 'btn-outline-light'} me-2`}
                                                    onClick={handleToggleRounding}
                                                    title={formData.enable_rounding ? "Rounding Enabled" : "Rounding Disabled"}
                                                >
                                                    <RotateCcw size={14} className="me-1" />
                                                    {formData.enable_rounding ? 'Rounded' : 'Actual'}
                                                </button>
                                                <h3 className="mb-0 text-white">
                                                    {formatCurrency(formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total)}
                                                </h3>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Additional Discount</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={formData.discount}
                                        onChange={(e) => handleDiscountAmountChange(e.target.value)}
                                        min="0"
                                        step="1"
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Shipping</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={formData.shipping}
                                        onChange={(e) => handleShippingChange(e.target.value)}
                                        min="0"
                                        step="1"
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Adjustment (+/-)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={formData.adjustment}
                                        onChange={(e) => handleAdjustmentChange(e.target.value)}
                                        step="1"
                                    />
                                </div>
                            </div>

                            <div className="row g-3 mb-4">
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Notes</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={formData.notes}
                                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                        placeholder="Additional notes"
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Terms & Conditions</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={formData.terms_conditions}
                                        onChange={(e) => setFormData(prev => ({ ...prev, terms_conditions: e.target.value }))}
                                        placeholder="Terms and conditions"
                                    />
                                </div>
                            </div>

                            <div className="text-end">
                                <button type="button" className="btn btn-secondary me-2" onClick={handleCancel}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={saving || !hasChanges()}
                                >
                                    {saving ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save size={16} className="me-2" />
                                            Update Quotation
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

EditQuotation.propTypes = {
    quotationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onSuccess: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    isOpen: PropTypes.bool
};

export default EditQuotation;